import { useCallback, useMemo } from "react";

// Third parties
import { useSelector } from "react-redux";
import { Stripe, StripeElements } from "@stripe/stripe-js";

// Evergiving
import {
  isDirectDebitSortCode,
  isDirectDebitNoSortCode,
  isFullBankRouting,
  PaymentFormConfig,
  DirectDebitType,
  isAccountNumberRouting,
} from "@evergiving/common/dist/es2015/form";

import {
  BankAccountIdentification,
  PaymentSourceKind,
  Versioned,
} from "@evergiving/common/dist/es2015/pledge";

// Redux
import { RootState } from "@/lib/redux/types";
import { WalletKind } from "@/lib/misc/walletKind";
import { ColombianBankCodes } from "@evergiving/common/dist/es2015/ValidatorBankAccountCO";

export interface StripePaymentContext {
  paymentElements: StripeElements;
  stripeInstance: Stripe;

  /**
   * Which wallet the donor actually paid with, read from the Express Checkout
   * confirm event. The submitted payment source names it, so the submission
   * lambda can check it against what the link authorises.
   */
  walletKind: WalletKind;
}

interface EssentialCreditCardPaymentFields {
  holderName: Versioned<string>;
  primaryAccountNumber: Versioned<string>;
  expiry: Versioned<string>;
}

interface PartialCreditCardPaymentFields {
  holderName: Versioned<string | undefined>;
  primaryAccountNumber: Versioned<string | undefined>;
  expiry: Versioned<string | undefined>;
}

interface EssentialDirectDebitNonIbanPaymentWithSortCodeFields {
  directDebitAccountHolder: Versioned<string>;
  directDebitAccountNumber: Versioned<string>;
  directDebitSortCode: Versioned<string>;
  directDebitIBAN: Versioned<string | undefined>;
  directDebitFinancialInstitution: Versioned<ColombianBankCodes | undefined>;
  directDebitAccountType: Versioned<string | undefined>;
}

interface EssentialDirectDebitNonIbanPaymentWithoutSortCodeFields {
  directDebitAccountHolder: Versioned<string>;
  directDebitAccountNumber: Versioned<string>;
  directDebitSortCode: Versioned<string | undefined>;
  directDebitIBAN: Versioned<string | undefined>;
  directDebitFinancialInstitution: Versioned<ColombianBankCodes | undefined>;
  directDebitAccountType: Versioned<string>;
}

interface EssentialDirectDebitIbanPaymentFields {
  directDebitAccountHolder: Versioned<string>;
  directDebitAccountNumber: Versioned<string | undefined>;
  directDebitSortCode: Versioned<string | undefined>;
  directDebitIBAN: Versioned<string>;
  directDebitFinancialInstitution: Versioned<ColombianBankCodes | undefined>;
  directDebitAccountType: Versioned<string | undefined>;
}

export interface PartialDirectDebitPaymentFields {
  directDebitAccountHolder: Versioned<string | undefined>;
  directDebitAccountNumber: Versioned<string | undefined>;
  directDebitSortCode: Versioned<string | undefined>;
  directDebitIBAN: Versioned<string | undefined>;
  directDebitFinancialInstitution: Versioned<ColombianBankCodes | undefined>;
  directDebitAccountType: Versioned<string | undefined>;
}

interface UsePaymentCommonProps {
  formConfig: PaymentFormConfig;
}

type AccountIdentificationFromFieldsTypeGuard = (
  input: PartialDirectDebitPaymentFields
) => BankAccountIdentification | undefined;

type CanSubmitCreditCardPaymentTypeGuard = (
  input: PartialCreditCardPaymentFields
) => input is EssentialCreditCardPaymentFields;

type CanSubmitDirectDebitPaymentTypeGuard = (
  input: PartialDirectDebitPaymentFields
) => input is
  | EssentialDirectDebitIbanPaymentFields
  | EssentialDirectDebitNonIbanPaymentWithoutSortCodeFields
  | EssentialDirectDebitNonIbanPaymentWithSortCodeFields;

type CanSubmitIbanPaymentTypeGuard = (
  input: PartialDirectDebitPaymentFields
) => input is EssentialDirectDebitIbanPaymentFields;

interface UsePaymentCommonReturn {
  accountIdentificationFromFields: AccountIdentificationFromFieldsTypeGuard;
  canSubmitCreditCardPayment: CanSubmitCreditCardPaymentTypeGuard;
  canSubmitDirectDebitPayment: CanSubmitDirectDebitPaymentTypeGuard;
  canSubmitIbanPayment: CanSubmitIbanPaymentTypeGuard;
  creditCardPaymentFields: PartialCreditCardPaymentFields;
  directDebitPaymentFields: PartialDirectDebitPaymentFields;
  paymentSourceKind: PaymentSourceKind | undefined;
}

function normalizeCardExpiry(
  cardExpiry: Versioned<string | undefined>
): Versioned<string | undefined> {
  const { value } = cardExpiry;
  if (value === undefined) {
    return cardExpiry;
  }
  const components = value.split("-");
  const year = components[0].replace(/\D/g, "");
  const month = components[1].replace(/\D/g, "");
  if (year.length === 0 || month.length === 0) {
    return { ...cardExpiry, value: undefined };
  }
  const yearNumber = Number(year) + (year.length >= 4 ? 0 : 2000);
  const monthNumber = Number(month);

  return { ...cardExpiry, value: `${yearNumber}-${monthNumber}` };
}

export const usePaymentCommon = ({
  formConfig,
}: UsePaymentCommonProps): UsePaymentCommonReturn => {
  const holderName = useSelector((state: RootState) => state.cardHolder);
  const primaryAccountNumber = useSelector(
    (state: RootState) => state.cardNumber
  );
  const expiry = useSelector((state: RootState) =>
    normalizeCardExpiry(state.cardExpiry)
  );

  const paymentSourceKind = useSelector(
    (state: RootState) => state.paymentSourceKind
  );
  const directDebitAccountHolder = useSelector(
    (state: RootState) => state.directDebitAccountHolder
  );
  const directDebitAccountNumber = useSelector(
    (state: RootState) => state.directDebitAccountNumber
  );
  const directDebitSortCode = useSelector(
    (state: RootState) => state.directDebitSortCode
  );
  const directDebitIBAN = useSelector(
    (state: RootState) => state.directDebitIBAN
  );
  const directDebitFinancialInstitution = useSelector(
    (state: RootState) => state.directDebitFinancialInstitution
  );
  const directDebitAccountType = useSelector(
    (state: RootState) => state.directDebitAccountType
  );
  const ibanAppraisal = useSelector((state: RootState) => state.ibanAppraisal);

  const creditCardPaymentFields = useMemo(
    () => ({
      holderName,
      primaryAccountNumber,
      expiry,
    }),
    [holderName, primaryAccountNumber, expiry]
  );

  const directDebitPaymentFields = useMemo(
    () => ({
      directDebitAccountHolder,
      directDebitAccountNumber,
      directDebitSortCode,
      directDebitIBAN,
      directDebitFinancialInstitution,
      directDebitAccountType,
    }),
    [
      directDebitAccountHolder,
      directDebitAccountNumber,
      directDebitSortCode,
      directDebitIBAN,
      directDebitFinancialInstitution,
      directDebitAccountType,
    ]
  );

  const canSubmitCreditCardPayment = (
    input: PartialCreditCardPaymentFields
  ): input is EssentialCreditCardPaymentFields =>
    input.holderName.value !== undefined &&
    input.primaryAccountNumber.value !== undefined &&
    input.expiry.value !== undefined;

  const canSubmitNonIbanPaymentWithSortCode = (
    input: PartialDirectDebitPaymentFields
  ): input is EssentialDirectDebitNonIbanPaymentWithSortCodeFields =>
    input.directDebitAccountHolder.value !== undefined &&
    input.directDebitAccountNumber.value !== undefined &&
    input.directDebitSortCode.value !== undefined;

  const canSubmitNonIbanPaymentWithoutSortCode = (
    input: PartialDirectDebitPaymentFields
  ): input is EssentialDirectDebitNonIbanPaymentWithoutSortCodeFields => {
    const basicFieldsValid =
      input.directDebitAccountHolder.value !== undefined &&
      input.directDebitAccountNumber.value !== undefined;
    if (!basicFieldsValid) {
      return false;
    }

    const directDebitConfig = formConfig.payment.directDebit;
    if (directDebitConfig && isDirectDebitNoSortCode(directDebitConfig)) {
      if (isFullBankRouting(directDebitConfig)) {
        return (
          input.directDebitAccountType.value !== undefined &&
          input.directDebitFinancialInstitution.value !== undefined
        );
      }

      if (isAccountNumberRouting(directDebitConfig)) {
        return input.directDebitAccountNumber.value !== undefined;
      }

      return false;
    }

    return false;
  };

  const canSubmitIbanPayment = (
    input: PartialDirectDebitPaymentFields
  ): input is EssentialDirectDebitIbanPaymentFields =>
    input.directDebitAccountHolder.value !== undefined &&
    input.directDebitIBAN.value !== undefined;

  const accountIdentificationFromFields = (
    input: PartialDirectDebitPaymentFields
  ): BankAccountIdentification | undefined => {
    if (formConfig.payment.directDebit?.type === DirectDebitType.IBAN) {
      if (canSubmitIbanPayment(input)) {
        return {
          iban: input.directDebitIBAN.value,
          validationResult: ibanAppraisal,
        };
      }
    }
    if (canSubmitNonIbanPaymentWithSortCode(input)) {
      return {
        accountNumber: input.directDebitAccountNumber.value,
        bankCode: input.directDebitSortCode.value,
      };
    }
    if (canSubmitNonIbanPaymentWithoutSortCode(input)) {
      return {
        accountNumber: input.directDebitAccountNumber.value,
        accountType: input.directDebitAccountType.value,
        financialInstitution: input.directDebitFinancialInstitution.value,
      };
    }
    return undefined;
  };

  const canSubmitDirectDebitPayment = useCallback(
    (
      input: PartialDirectDebitPaymentFields
    ): input is
      | EssentialDirectDebitIbanPaymentFields
      | EssentialDirectDebitNonIbanPaymentWithoutSortCodeFields
      | EssentialDirectDebitNonIbanPaymentWithSortCodeFields => {
      if (formConfig.payment.directDebit?.type === DirectDebitType.IBAN) {
        return canSubmitIbanPayment(input);
      }

      return formConfig.payment.directDebit !== undefined &&
        isDirectDebitSortCode(formConfig.payment.directDebit)
        ? canSubmitNonIbanPaymentWithSortCode(input)
        : canSubmitNonIbanPaymentWithoutSortCode(input);
    },

    [formConfig.payment.directDebit?.type]
  );

  return {
    accountIdentificationFromFields,
    canSubmitCreditCardPayment,
    canSubmitDirectDebitPayment,
    canSubmitIbanPayment,
    creditCardPaymentFields,
    directDebitPaymentFields,
    paymentSourceKind,
  };
};
